import { Asset } from '../../../models/image/dto/asset';
import { MediaManagerAssetLibraryDto } from '../../../models/image/dto/media-manager-asset-library-dto';
import { MediaManagerFileSystem } from '../../../models/media-manager/media-manager-file-system';
import { findAssetInLibrary } from '../mutations/asset-library-lookups';
import { getProtectedSpecialAncestorForAsset } from '../policy/special-folder-policy';

/** A single asset queued for deletion, paired with the canonical library entry the API delete keys on. */
export interface MediaManagerPreparedAssetDelete {
  assetUniqueId: string;
  asset: Asset;
}

/**
 * Verify a batch of asset deletes. Reports missing/invalid ids, missing library entries, missing API delete
 * identifiers (id + md5Hash), missing filesystem presence, and protected-ancestor policy.
 */
export function preflightDeleteAssetsValidation(
  assetUniqueIds: string[],
  preparedDeletes: MediaManagerPreparedAssetDelete[] | null,
  filesystem: MediaManagerFileSystem,
  assetLibrary: MediaManagerAssetLibraryDto
): string | null {
  const errors: string[] = [];
  const preparedDeletesById = new Map(
    (preparedDeletes || []).map(preparedDelete => [preparedDelete.assetUniqueId, preparedDelete])
  );
  for (const assetUniqueId of assetUniqueIds) {
    if (!assetUniqueId?.trim()) {
      errors.push('Asset ID is not valid');
      continue;
    }

    const preparedDelete = preparedDeletesById.get(assetUniqueId);
    const canonicalAsset = preparedDelete?.asset;
    if (!canonicalAsset) {
      errors.push(`MediaManager asset '${assetUniqueId}' could not be found in assetLibrary`);
      continue;
    }
    if (!canonicalAsset.id?.trim() || !canonicalAsset.md5Hash?.trim()) {
      errors.push(`MediaManager asset '${assetUniqueId}' does not have a valid API delete identifier`);
    }
    if (!filesystem.assetExists(assetUniqueId)) {
      errors.push(`Asset '${assetUniqueId}' does not exist in the filesystem`);
    }

    const protectedAncestorFolder = getProtectedSpecialAncestorForAsset(canonicalAsset, filesystem, assetLibrary);
    if (protectedAncestorFolder) {
      errors.push(
        `Cannot delete asset '${assetUniqueId}' because it is inside protected folder "${protectedAncestorFolder.name}"`
      );
    }
  }
  return errors.length ? errors.join('. ') : null;
}

/**
 * Prepare canonical delete records for a batch of asset unique ids. Assets missing from the library are
 * silently dropped; the preflight step below re-reports them as errors.
 */
export function prepareAssetDeletes(
  assetUniqueIds: string[],
  assetLibrary: MediaManagerAssetLibraryDto
): MediaManagerPreparedAssetDelete[] {
  const prepared: MediaManagerPreparedAssetDelete[] = [];
  for (const assetUniqueId of assetUniqueIds) {
    const asset = findAssetInLibrary(assetUniqueId, assetLibrary);
    if (asset) {
      prepared.push({ assetUniqueId, asset });
    }
  }
  return prepared;
}
